import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  login,
  requestPasswordReset,
  setAuthToken,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { useMe } from "@/features/auth/useMe";
import { unscopedKey } from "@/lib/queryClient";

/**
 * M9a A1–A7 — `/login` real-UI.
 *
 * Replaces the M4 `<InlineLoginPanel>` placeholder. Three concerns:
 *   1. Username + Password form → POST /api/login → setAuthToken
 *      → invalidate all queries → navigate to `?next=` (or `/hub`,
 *      which redirects to the user's role-based home).
 *   2. "Forgot password?" link → `<PasswordResetRequestModal>` →
 *      POST /api/auth/password-reset/request. Generic
 *      enumeration-safe response. Dev-token Copy-link when the
 *      backend returns `reset_token` (TKTIC_DEV_TOKEN_DELIVERY=1).
 *   3. Session-resume on mount — if `/api/me` already returns a
 *      user (cached token still valid), skip the form and go to
 *      `next` immediately.
 *
 * The page does NOT mount the AdminShell or any other authed
 * shell — it's a standalone route at /login.
 */
export function LoginPage() {
  const me = useMe();
  const navigate = useNavigate({ from: "/login" });
  const queryClient = useQueryClient();
  const search = useSearch({ from: "/login" });
  const nextPath = sanitizeNextPath(search.next);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  // A5 — session-resume. If /api/me already returns a user, the
  // operator is already signed in (probably hit /login by mistake
  // or via a bookmark). Send them where they were trying to go.
  useEffect(() => {
    if (me.data?.user) {
      void navigate({ to: nextPath, replace: true });
    }
  }, [me.data, nextPath, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await login({ username, password });
      setAuthToken(resp.token);
      // Invalidate everything so sibling caches refetch with the
      // new auth context. Includes the unscoped `me` query so the
      // session-resume effect picks up the fresh user.
      await queryClient.invalidateQueries();
      // Belt + suspenders: explicitly invalidate `me` since some
      // surfaces gate on `useMe().data` directly.
      await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      void navigate({ to: nextPath, replace: true });
    } catch (err) {
      setError(formatLoginErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="login-page"
      className="min-h-screen bg-canvas flex items-center justify-center p-4"
    >
      <section
        aria-label="Sign in to Tktic"
        className="w-full max-w-md rounded-lg border border-rule bg-surface p-6 flex flex-col gap-4"
      >
        <header className="flex flex-col gap-1">
          <h1 className="text-xl font-display tracking-tight text-ink">
            Sign in
          </h1>
          <p className="text-ink-muted text-xs">
            Coach + admin + viewer surfaces all sign in here.
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          data-testid="login-form"
          className="flex flex-col gap-3"
        >
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-display uppercase tracking-wider text-ink-muted">
              Username
            </span>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              data-testid="login-username"
              className="rounded border border-rule bg-bg-card px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-display uppercase tracking-wider text-ink-muted">
              Password
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="login-password"
              className="rounded border border-rule bg-bg-card px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>

          {error ? (
            <p
              data-testid="login-error"
              className="text-destructive text-xs font-mono"
            >
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="self-end"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <footer className="flex items-center justify-between text-[0.65rem] font-mono text-ink-muted pt-1 border-t border-rule/60">
          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            data-testid="login-forgot"
            className="hover:text-ink hover:underline"
          >
            Forgot password?
          </button>
          {nextPath && nextPath !== "/" && nextPath !== "/hub" ? (
            <span data-testid="login-next-target">
              → {nextPath}
            </span>
          ) : null}
        </footer>
      </section>

      {forgotOpen ? (
        <PasswordResetRequestModal
          initialUsername={username}
          onClose={() => setForgotOpen(false)}
        />
      ) : null}
    </main>
  );
}

// ---------------------------------------------------------------------------
// A7 — Forgot password request modal
// ---------------------------------------------------------------------------

function PasswordResetRequestModal({
  initialUsername,
  onClose,
}: {
  initialUsername: string;
  onClose: () => void;
}) {
  const [username, setUsername] = useState(initialUsername);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  // Privacy invariant: the dev-token surfaces ONLY when the
  // backend response carries it (TKTIC_DEV_TOKEN_DELIVERY=1).
  // Never logged; surfaces via a gated Copy-link control.
  const [devResetToken, setDevResetToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (submitting) return;
    if (!username.trim()) {
      setError("Username is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await requestPasswordReset({ username: username.trim() });
      // The response is enumeration-safe — same shape regardless
      // of whether the username exists. Only the dev-token branch
      // distinguishes.
      setSuccess(true);
      if (resp.reset_token) {
        setDevResetToken(resp.reset_token);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Could not send reset request.",
        );
      } else {
        setError("Network error. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      title="Reset password"
      message={
        success ? (
          <div className="flex flex-col gap-2">
            <p
              data-testid="login-forgot-success"
              className="text-ink text-sm"
            >
              If an account exists for that username, reset
              instructions are on the way. The reset link goes to
              the email on file.
            </p>
            {devResetToken ? (
              <div
                data-testid="login-forgot-dev-token"
                className="flex flex-col gap-1 rounded border border-rule bg-surface/40 p-2 text-[0.65rem] font-mono"
              >
                <span className="text-ink-muted">
                  Dev reset URL:
                </span>
                <code className="text-ink break-all">
                  {buildResetUrl(devResetToken)}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard?.writeText(
                      buildResetUrl(devResetToken),
                    );
                  }}
                  data-testid="login-forgot-dev-copy"
                  className="self-start rounded border border-rule text-ink-muted hover:text-ink hover:bg-surface px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider"
                >
                  Copy link
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-display uppercase tracking-wider text-ink-muted">
                Username
              </span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                data-testid="login-forgot-username"
                className="rounded border border-rule bg-surface px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>
            <p className="text-[0.6rem] font-mono text-ink-muted">
              We'll send reset instructions to the email on file
              for that username.
            </p>
            {error ? (
              <p
                data-testid="login-forgot-error"
                className="text-destructive text-xs font-mono"
              >
                {error}
              </p>
            ) : null}
          </div>
        )
      }
      confirmLabel={
        success
          ? "Close"
          : submitting
            ? "Sending…"
            : "Send reset instructions"
      }
      onConfirm={() => {
        if (success) onClose();
        else void submit();
      }}
      onClose={onClose}
      data-testid="login-forgot-modal"
    />
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/** Sanitize `?next=` — only allow same-origin paths starting with
 * `/`. Prevents an attacker from crafting `/login?next=//evil.com`
 * and bouncing operators offsite after sign-in.
 *
 * When `?next=` is absent or invalid, defaults to `/hub` — the
 * role-based redirect — so a bare sign-in lands each user on their
 * own home (Coach Review / Feedback / Admin) rather than the public
 * marketing landing at `/`.
 *
 * Rejects (falling back to `/hub`):
 *   - non-strings, empty
 *   - protocol-relative (`//evil.com`) and absolute URLs
 *   - backslash-prefixed (`/\evil.com`) — some browsers resolve this
 *     as `//evil.com`
 *   - encoded backslash (`/%5Cevil.com`) — same risk after decode
 *   - auth-entry routes such as `/login` — prevents recursive
 *     `/login?next=/login?next=...` growth when users click Login
 *     while already on the sign-in page
 *   - control characters (`\x00`–`\x1f`, `\x7f`) — newlines / NULs
 *   - anything over 2048 chars
 */
export function sanitizeNextPath(next: string | undefined): string {
  if (!next || typeof next !== "string") return "/hub";
  if (next.length > 2048) return "/hub";
  if (!next.startsWith("/") || next.startsWith("//")) return "/hub";
  // `/\foo` → some browsers normalize the backslash to `/`, giving
  // `//foo` and bouncing offsite. Reject ANY backslash in the path.
  if (next.includes("\\")) return "/hub";
  // `/%5C…` decodes to `/\…` — same risk. Reject percent-encoded
  // backslash before any later code calls `decodeURIComponent`.
  if (/%5c/i.test(next)) return "/hub";
  // A login route is never a useful post-login destination. Treating
  // it as valid makes the header's Login link recursively encode the
  // current login URL into `?next=`.
  if (isAuthEntryPath(next)) return "/hub";
  // Reject control characters (NUL, CR, LF, etc.) which can break
  // header / URL parsers downstream.
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(next)) return "/hub";
  return next;
}

function isAuthEntryPath(path: string): boolean {
  return /^\/(?:login|reset-password|verify-email)(?:[/?#]|$)/.test(path);
}

function buildResetUrl(token: string): string {
  if (typeof window === "undefined") return `/reset-password?token=${token}`;
  return `${window.location.origin}/reset-password?token=${encodeURIComponent(token)}`;
}

function formatLoginErr(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "Wrong username or password.";
    if (err.status === 429) {
      return "Too many sign-in attempts. Try again in a minute.";
    }
    return typeof err.detail === "string"
      ? err.detail
      : `Sign-in failed (HTTP ${err.status}).`;
  }
  return "Network error. Try again.";
}
